import { Link } from "react-router-dom";
import { LeafMark } from "./LeafMark";
import { MessageCircle, Linkedin, Instagram, Facebook } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const SOCIAL_ICONS = { linkedin: Linkedin, instagram: Instagram, facebook: Facebook };

export const Footer = () => {
    const { t, site: siteData } = useLang();
    const { SITE, EXPLORE_LINKS, PATIENT_SERVICE_LINKS, LEGAL_LINKS, SOCIAL_LINKS, DISCLAIMER_TEXT } = siteData;
    return (
    <footer
        data-testid="site-footer"
        className="bg-forest text-ivory relative overflow-hidden"
    >
        <div
            aria-hidden="true"
            className="pointer-events-none absolute -right-24 -top-24 w-[480px] h-[480px] rounded-full bg-pine/40 blur-3xl"
        />
        <div className="container-site relative pt-16 sm:pt-20 pb-10">
            <div className="grid gap-12 lg:grid-cols-12">
                <div className="lg:col-span-4">
                    <div className="flex items-center gap-3">
                        <LeafMark className="w-9 h-9 text-natural" />
                        <p className="font-serif text-3xl sm:text-4xl leading-tight">
                            Univers<span className="italic text-natural">Heal</span>
                        </p>
                    </div>
                    <p className="mt-4 font-serif italic text-xl text-natural">
                        {SITE.tagline}
                    </p>
                    <p className="mt-5 text-sm leading-relaxed text-ivory/60 max-w-sm">
                        {t("footer.description", "International healthcare coordination — connecting patients with leading specialists and hospitals across India. Starting in Kerala, God's Own Country.")}
                    </p>
                    <div className="mt-7 flex items-center gap-3">
                        {SOCIAL_LINKS.map((s) => {
                            const Icon = SOCIAL_ICONS[s.icon];
                            return (
                                <a
                                    key={s.label}
                                    href={s.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={`${s.label} (placeholder link)`}
                                    title={`${s.label} — placeholder`}
                                    data-testid={`footer-social-${s.label.toLowerCase()}`}
                                    className="flex items-center justify-center w-10 h-10 rounded-xl border border-ivory/20 text-ivory/70 hover:text-forest hover:bg-ivory transition-all duration-300"
                                >
                                    <Icon className="w-[18px] h-[18px]" />
                                </a>
                            );
                        })}
                    </div>
                </div>

                <div className="lg:col-span-2">
                    <h3 className="eyebrow text-natural/80 mb-5">{t("footer.quick_links", "Quick Links")}</h3>
                    <ul className="space-y-3">
                        {EXPLORE_LINKS.map((link) => (
                            <li key={link.label}>
                                <Link
                                    to={link.path}
                                    data-testid={`footer-link-explore-${link.path === "/" ? "home" : link.path.slice(1).replace(/\//g, "-")}`}
                                    className="text-sm text-ivory/70 hover:text-ivory transition-colors duration-200"
                                >
                                    {link.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="lg:col-span-3">
                    <h3 className="eyebrow text-natural/80 mb-5">{t("footer.patient_services", "Patient Services")}</h3>
                    <ul className="space-y-3">
                        {PATIENT_SERVICE_LINKS.map((link) => (
                            <li key={link.label}>
                                <Link
                                    to={link.path}
                                    data-testid={`footer-link-services-${link.path === "/" ? "home" : link.path.slice(1).replace(/\//g, "-")}`}
                                    className="text-sm text-ivory/70 hover:text-ivory transition-colors duration-200"
                                >
                                    {link.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="lg:col-span-3">
                    <h3 className="eyebrow text-natural/80 mb-5">{t("footer.contact", "Contact")}</h3>
                    <ul className="space-y-3 text-sm text-ivory/70">
                        <li data-testid="footer-contact-email">{SITE.emailDisplay}</li>
                        <li data-testid="footer-contact-phone">{SITE.phoneDisplay}</li>
                        <li data-testid="footer-contact-office">{SITE.officeDisplay}</li>
                    </ul>
                    <a
                        href={SITE.whatsappUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="footer-whatsapp"
                        className="mt-5 inline-flex btn-outline-light !py-2.5 !px-4 text-xs"
                    >
                        <MessageCircle className="w-4 h-4" />
                        {t("common.chat_whatsapp", "Chat on WhatsApp")}
                    </a>
                </div>
            </div>

            <div className="mt-16 pt-8 border-t border-ivory/10 space-y-4">
                <p className="text-xs leading-relaxed text-ivory/45 max-w-3xl">
                    {DISCLAIMER_TEXT}
                </p>
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <p data-testid="footer-copyright" className="text-xs text-ivory/60">
                        {t("footer.copyright", "© UniversHeal. All rights reserved.")}
                    </p>
                    <div className="flex flex-wrap gap-x-5 gap-y-2">
                        {LEGAL_LINKS.map((l) => (
                            <Link
                                key={l.path}
                                to={l.path}
                                data-testid={`footer-link-legal-${l.path.slice(1).replace(/\//g, "-")}`}
                                className="text-xs text-ivory/60 hover:text-ivory transition-colors"
                            >
                                {l.label}
                            </Link>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    </footer>
    );
};
